'use client';

import { TemplateLibraryModal } from '@/@studio-ai/components/planner-mode/template-library-modal';
import { InsightTemplate } from '@/@studio-ai/types';
import { Card, CardBody, Image, Link, Spinner } from '@heroui/react';
import { Icon } from '@iconify/react';
import { AnimatePresence, motion } from 'framer-motion';
import NextImage from 'next/image';
import { useState } from 'react';
import { INITIAL_VISIBLE_COUNT } from '../../constants';

function TemplateImage({ template }: { template: InsightTemplate }) {
    if (template.imageDataUri) {
        return (
            <Image
                src={template.imageDataUri}
                as={NextImage}
                alt={template.title}
                width={64}
                height={64}
                radius="md"
                removeWrapper
                classNames={{
                    img: 'h-16 w-16 shrink-0 object-cover',
                }}
            />
        );
    }
    return (
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-medium bg-default-100">
            <Icon icon="lucide:sparkles" width={22} height={22} className="text-default-500" />
        </div>
    );
}

function TemplateGrid({
    templates,
    onSelectTemplate,
    isSelecting,
    selectedTemplateId,
}: {
    templates: InsightTemplate[];
    onSelectTemplate: (template: InsightTemplate) => Promise<void> | void;
    isSelecting: boolean;
    selectedTemplateId: number | null;
}) {
    return (
        <div className="grid w-full grid-cols-3 gap-3">
            <AnimatePresence initial={false}>
                {templates.map((template, index) => (
                    <motion.div
                        key={template.id}
                        className="min-w-0"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2, delay: index * 0.04 }}
                    >
                        <Card
                            fullWidth
                            isPressable
                            isDisabled={isSelecting}
                            onPress={() => onSelectTemplate(template)}
                            radius="lg"
                            shadow="none"
                            classNames={{
                                base: 'h-full border border-default-200/60 bg-white transition-all hover:bg-mfour-green/10 hover:border-success-300 hover:shadow-sm hover:ring-1 hover:ring-success-300/40 dark:bg-default-50/90',
                            }}
                        >
                            <CardBody className="flex-row items-center gap-3 overflow-hidden p-3">
                                <TemplateImage template={template} />
                                <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
                                    <span className="truncate text-mini font-semibold text-foreground">
                                        {template.title}
                                    </span>
                                    <span className="line-clamp-2 text-tiny leading-snug text-default-500">
                                        {template.subtitle}
                                    </span>
                                    {isSelecting && selectedTemplateId === template.id && (
                                        <div className="mt-1 flex items-center gap-2 text-tiny font-medium text-default-600">
                                            <Spinner size="sm" />
                                            <span>Starting template...</span>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
}

export function TemplateCards({
    templates,
    onSelectTemplate,
}: {
    templates: InsightTemplate[];
    onSelectTemplate: (template: InsightTemplate) => Promise<void> | void;
}) {
    const [isLibraryOpen, setIsLibraryOpen] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

    if (templates.length === 0) return null;

    const visibleTemplates = templates.slice(0, INITIAL_VISIBLE_COUNT);

    async function handleTemplatePress(template: InsightTemplate) {
        if (isSelecting) return;

        setIsSelecting(true);
        setSelectedTemplateId(template.id);
        try {
            await Promise.resolve(onSelectTemplate(template));
        } finally {
            setIsSelecting(false);
            setSelectedTemplateId(null);
        }
    }

    async function handleLibrarySelect(template: InsightTemplate) {
        setIsLibraryOpen(false);
        await handleTemplatePress(template);
    }

    return (
        <div className="flex w-full flex-col gap-3">
            <TemplateGrid
                templates={visibleTemplates}
                onSelectTemplate={handleTemplatePress}
                isSelecting={isSelecting}
                selectedTemplateId={selectedTemplateId}
            />
            <p className="text-center text-tiny text-default-500">
                Use an insights template above or{' '}
                <Link
                    as="button"
                    type="button"
                    isDisabled={isSelecting}
                    onPress={() => setIsLibraryOpen(true)}
                    color="foreground"
                    className="inline-flex items-baseline gap-1 text-tiny font-medium hover:text-success-600"
                >
                    <span className="underline decoration-1 underline-offset-2">
                        View all templates
                    </span>
                    <Icon
                        icon="lucide:arrow-right"
                        width={12}
                        height={12}
                        aria-hidden="true"
                        className="self-center"
                    />
                </Link>
            </p>
            <TemplateLibraryModal
                isOpen={isLibraryOpen}
                onOpenChange={setIsLibraryOpen}
                templates={templates}
                onSelectTemplate={handleLibrarySelect}
                isSelecting={isSelecting}
                selectedTemplateId={selectedTemplateId}
            />
        </div>
    );
}
